import { useState } from "react";
import { defaultFilename, exportProject, saveBlob, type AugmentedChoice, type ExportFormat } from "../api/export";
import { testPct } from "../lib/dashboard";
import { Dialog } from "../ui/Dialog";
import { useDialogs } from "../ui/dialogs";
import { useStatus } from "../state/status";

const FORMATS: [string, ExportFormat][] = [
  ["COCO JSON (.json)", "coco"], ["YOLO TXT (.zip)", "yolo"], ["Pascal VOC XML (.zip)", "voc"], ["Full dataset ZIP (images + COCO)", "zip"],
];

/** export_dialog.py. The file arrives as a browser download instead of a save-as dialog. */
export function ExportDialog({ projectId, onClose }: { projectId: number; onClose: () => void }) {
  const dlg = useDialogs();
  const { say } = useStatus();
  const [fmt, setFmt] = useState<ExportFormat>("coco");
  const [split, setSplit] = useState(false);
  const [train, setTrain] = useState(80);
  const [val, setVal] = useState(10);
  const [busy, setBusy] = useState(false);
  const [augmented, setAugmented] = useState<AugmentedChoice>(null);
  const test = testPct(train, val);

  const run = async () => {
    if (split && train + val > 100) { await dlg.alert({ title: "Check the split", text: "Training and validation together cannot be more than 100%." }); return; }
    setBusy(true);
    try {
      const blob = await exportProject(projectId, fmt, { split, trainPct: train / 100, valPct: val / 100 }, augmented);
      const name = defaultFilename(fmt, split);
      saveBlob(blob, name);
      say(`Dataset exported as ${name}`);
      onClose();
    } catch (e) {
      await dlg.alert({ title: "Could not export the dataset", text: (e as Error).message });
    } finally { setBusy(false); }
  };

  return (
    <Dialog title="Export dataset" onClose={onClose} width={400} testId="export">
      <div className="muted" style={{ marginBottom: 6 }}>Format</div>
      {FORMATS.map(([label, value]) => (
        <label key={value} className="check"><input type="radio" name="fmt" checked={fmt === value} onChange={() => setFmt(value)} data-testid={`fmt-${value}`} />{label}</label>
      ))}
      <hr style={{ border: 0, borderTop: "1px solid var(--border)", margin: "12px 0" }} />
      <label className="check"><input type="checkbox" checked={split} onChange={(e) => setSplit(e.target.checked)} data-testid="split" />Split into Train / Validation / Test sets</label>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
        <span>Train %</span>
        <input className="input" type="number" min={1} max={98} value={train} disabled={!split} style={{ width: 70 }} onChange={(e) => setTrain(Number(e.target.value))} data-testid="train-pct" />
        <span>Val %</span>
        <input className="input" type="number" min={1} max={98} value={val} disabled={!split} style={{ width: 70 }} onChange={(e) => setVal(Number(e.target.value))} data-testid="val-pct" />
        <span className="muted" style={test < 0 ? { color: "var(--danger)" } : undefined} data-testid="test-pct">
          {test < 0 ? "Test: invalid (over 100%)" : `Test: ${test}%`}
        </span>
      </div>
      <hr style={{ border: 0, borderTop: "1px solid var(--border)", margin: "12px 0" }} />
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}
        title="Copies made on the Augment page. With a split, each copy goes into the same set as its original, so a validation or test image never has a copy of itself in training.">
        <span style={{ whiteSpace: "nowrap" }}>Augmented copies:</span>
        <select className="select" value={augmented ?? ""} onChange={(e) => setAugmented((e.target.value || null) as AugmentedChoice)} data-testid="export-augmented">
          <option value="">Include them</option>
          <option value="exclude">Leave them out</option>
          <option value="only">Only the copies</option>
        </select>
      </div>
      <div className="dialog-actions">
        <button className="btn" onClick={onClose}>Cancel</button>
        <button className="btn primary" disabled={busy} onClick={run} data-testid="export-go">{busy ? "Exporting…" : "Export"}</button>
      </div>
    </Dialog>
  );
}
